import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import Layout from '~/components/Layout'
import { El } from '~/components/Layout/style'
import { LIGHT_USER_ROLE_SLUG } from '~/Constants'
import { useAuth } from '~/routes/components/AuthProvider'
import BasicFarmerInfo from './BasicFarmerInfo'
import DairyQuery from '~/QueryHooks/DairyQuery'
import { FarmerCreateOptionsType, FarmersService } from '~/api/services/farmers'
import { handleErrors } from '~/utils/helpers'
import { useParams } from 'react-router'
import notify from '~/components/Notify'
import FormSection from './BasicFarmerInfo/FormSection'
import LocationForm from '~/pages/Dairy/DairyFactory/Managers/CreateManager/LocationForm'
import SectionNavigation from './SectionNavigation'

interface Props {
  farmerOptions: FarmerCreateOptionsType
  handleContractTypeChange: () => void
}

const SwissFarmerDetails: React.FC<Props> = ({ farmerOptions, handleContractTypeChange }) => {
  const { t } = useTranslation()
  const { id } = useParams()
  const { systemData: { logged: { roleSlug } } } = useAuth()

  const lightUserLogged = roleSlug === LIGHT_USER_ROLE_SLUG

  const [isBusy, setIsBusy] = useState(false)

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  const methods = useForm({
    mode: 'onChange'
  })

  useEffect(() => {
    fetchCountries()
    fetchDetails()
  }, [t])

  async function fetchDetails() {
    setIsBusy(true)
    try {
      const response = await FarmersService.getFarmerDetails(id)
      if (!response.data.farmer.districtId && response.data.farmer.zip) {
        const zipData = await FarmersService.getZipDistrictState({ zip: response.data.farmer.zip })
        response.data.farmer.districtId = zipData.data.id
      }
      methods.reset({
        ...response.data.farmer,
        farmerType: response.data.farmer.parentId ? 'sub' : 'main'
      })
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  async function onSubmit(data) {
    setIsBusy(true)
    try {
      if (!id && data?.farmerType === 'sub') {
        delete data.farmerGroupId
      }
      data.longitude = data?.longitude?.toString()?.replace(',', '.')
      data.latitude = data?.latitude?.toString()?.replace(',', '.')
      await FarmersService.updateFarmerDetails(id, data)
      if (methods.formState?.dirtyFields?.contractTypeId) {
        handleContractTypeChange()
      }
      notify(t('Successfully update farmer details'), 'success')
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderActions() {
    return <div style={{ display: 'flex', alignItems: 'baseline', flexGrow: 1, gap: 20 }}>
      <El.PageHeaderActionButton>{t('Update')}</El.PageHeaderActionButton>
    </div>
  }

  function renderForm() {

    return <>
      <BasicFarmerInfo />
      <FormSection id='location' header={t('Location')} headerIcon='address card'>
        <LocationForm countries={countries} collectionSiteOptions={farmerOptions} useGoogleMapLocation />
      </FormSection>
    </>
  }

  function renderNavigation() {
    const sections = [
      { id: 'basic-information', label: t('Basic Information'), icon: 'building' as const },
      { id: 'production', label: t('Production'), icon: 'factory' as const },
      { id: 'collection-transport', label: t('Logistik'), icon: 'truck' as const },
      { id: 'managers-certifications', label: t('Managers & Certifications'), icon: 'users' as const },
      { id: 'relationships', label: t('Relationships'), icon: 'sitemap' as const },
      { id: 'location', label: t('Location'), icon: 'address card' as const }
    ]
    return <SectionNavigation sections={sections} />
  }

  return <Layout
    childContainerSize={'auto'}
    useSpinner
    isBusy={isBusy || isLoadingCountries}
    title={renderNavigation()}
    onSubmit={methods.handleSubmit(onSubmit)}
    actions={!lightUserLogged && renderActions()}
  >
    <FormProvider {...methods}>
      {renderForm()}
    </FormProvider>
  </Layout>
}
export default SwissFarmerDetails